import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';
import type { ProductCardMediaNavigatePayload } from '@noths/polaris-design-system-candidate-components';

/**
 * How the interaction is named in the event, per DISC-4589.
 *
 * A hover has no direction to report: it reveals the second image from wherever the
 * track happens to be, so there is one name for it rather than two.
 */
type ProductMediaEventDetails = 'next media' | 'previous media' | 'hover';

const getEventDetails = ({
  direction,
  method,
}: ProductCardMediaNavigatePayload): ProductMediaEventDetails => {
  if (method === 'hover') {
    return 'hover';
  }

  return direction === 'prev' ? 'previous media' : 'next media';
};

/**
 * Reports the customer reaching another of a product's images on a listing.
 *
 * Sent per interaction rather than batched like impressions: the card already reports
 * a swipe once the track has settled and a hover once per card, so the events arrive
 * at the rate the customer generates them rather than at the rate the browser fires
 * scroll events.
 *
 * `item_id` carries the product's name, which is what the analytics definition asks
 * for. Every other product event on this page identifies a product by its code, so
 * this is deliberate rather than an oversight.
 */
export const trackProductMediaInteraction = (payload: ProductCardMediaNavigatePayload) => {
  // Defer off the interaction's critical path: fire-and-forget, and it gates nothing.
  deferTracking(() =>
    sendGAEvent({
      event: 'custom_event_v2',
      event_name: 'content_interaction',
      event_details: getEventDetails(payload),
      item_id: payload.product.title,
      content_type: 'multi image product card',
    }),
  );
};
